/**
 * FHIR R4 Constants — LOINC codes, system URIs, and PHQ-4 item definitions.
 * Used by builders to generate standards-compliant FHIR resources.
 */

export const FHIR_SYSTEMS = {
  loinc: 'http://loinc.org',
  snomed: 'http://snomed.info/sct',
  ucum: 'http://unitsofmeasure.org',
  observationCategory: 'http://terminology.hl7.org/CodeSystem/observation-category',
  areteCodeSystem: 'https://arete.health/fhir/CodeSystem/phenotype-patterns',
  phq4Questionnaire: 'http://arete.health/fhir/Questionnaire/phq4',
  phq4AnswerList: 'http://loinc.org/vs/LL358-3',
};

export interface LoincDef {
  code: string;
  display: string;
  unit: string;
  ucum: string;
}

/** LOINC codes for wearable and lab biomarkers tracked by Arete. */
export const LOINC_CODES: Record<string, LoincDef> = {
  hrv_sdnn:         { code: '80404-7', display: 'R-R interval.standard deviation (HRV)', unit: 'ms', ucum: 'ms' },
  resting_hr:       { code: '40443-4', display: 'Heart rate', unit: '/min', ucum: '/min' },
  sleep_duration:   { code: '93832-4', display: 'Sleep duration', unit: 'min', ucum: 'min' },
  rem_sleep:        { code: '93829-0', display: 'REM sleep duration', unit: 'min', ucum: 'min' },
  light_sleep:      { code: '93830-8', display: 'Light sleep duration', unit: 'min', ucum: 'min' },
  daily_steps:      { code: '41950-7', display: 'Number of steps in 24 hour', unit: 'steps/d', ucum: '/d' },
  exercise_minutes: { code: '89574-8', display: 'Exercise activity', unit: 'min/wk', ucum: 'min/wk' },
  phq4_total:       { code: '69724-3', display: 'PHQ-4 Total Score', unit: 'score', ucum: '{score}' },
  vitamin_d:        { code: '1989-3',  display: '25-hydroxyvitamin D', unit: 'ng/mL', ucum: 'ng/mL' },
  hscrp:            { code: '30522-7', display: 'CRP [Mass/volume] in Serum/Plasma by High sensitivity', unit: 'mg/L', ucum: 'mg/L' },
  magnesium:        { code: '2601-3',  display: 'Magnesium [Mass/volume] in Serum/Plasma', unit: 'mg/dL', ucum: 'mg/dL' },
  tsh:              { code: '3016-3',  display: 'TSH [Units/volume] in Serum/Plasma', unit: 'mIU/L', ucum: 'm[IU]/L' },
  free_t4:          { code: '3024-7',  display: 'Thyroxine free [Mass/volume] in Serum/Plasma', unit: 'ng/dL', ucum: 'ng/dL' },
  testosterone:     { code: '2986-8',  display: 'Testosterone [Mass/volume] in Serum/Plasma', unit: 'ng/dL', ucum: 'ng/dL' },
  hba1c:            { code: '4548-4',  display: 'Hemoglobin A1c', unit: '%', ucum: '%' },
  fasting_insulin:  { code: '20448-7', display: 'Insulin [Units/volume] in Serum/Plasma', unit: 'uIU/mL', ucum: 'u[IU]/mL' },
  triglycerides:    { code: '2571-8',  display: 'Triglycerides [Mass/volume] in Serum/Plasma', unit: 'mg/dL', ucum: 'mg/dL' },
  hdl:              { code: '2085-9',  display: 'HDL Cholesterol [Mass/volume] in Serum/Plasma', unit: 'mg/dL', ucum: 'mg/dL' },
  ldl:              { code: '2089-1',  display: 'LDL Cholesterol [Mass/volume] in Serum/Plasma', unit: 'mg/dL', ucum: 'mg/dL' },
  ferritin:         { code: '2276-4',  display: 'Ferritin [Mass/volume] in Serum/Plasma', unit: 'ng/mL', ucum: 'ng/mL' },
};

/** PHQ-4 questionnaire item definitions with LOINC linkIds. */
export const PHQ4_ITEMS = [
  { linkId: '44250-9', text: 'Little interest or pleasure in doing things' },
  { linkId: '44255-8', text: 'Feeling down, depressed, or hopeless' },
  { linkId: '69725-0', text: 'Feeling nervous, anxious or on edge' },
  { linkId: '68509-9', text: 'Not being able to stop or control worrying' },
];

/** PHQ-4 answer value set (0–3 Likert). */
export const PHQ4_ANSWERS = [
  { code: 'LA6568-5', display: 'Not at all' },
  { code: 'LA6569-3', display: 'Several days' },
  { code: 'LA6570-1', display: 'More than half the days' },
  { code: 'LA6571-9', display: 'Nearly every day' },
];

/** Map Arete lab marker names to LOINC keys. */
export const LAB_MARKER_TO_LOINC: Record<string, string> = {
  'Vitamin D': 'vitamin_d',
  'hsCRP': 'hscrp',
  'Magnesium': 'magnesium',
  'TSH': 'tsh',
  'Free T4': 'free_t4',
  'Testosterone': 'testosterone',
  'HbA1c': 'hba1c',
  'Insulin': 'fasting_insulin',
  'Triglycerides': 'triglycerides',
  'HDL': 'hdl',
  'LDL': 'ldl',
  'Ferritin': 'ferritin',
};
